import { useState, useEffect, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Maximize,
  Minimize,
  Play,
  Pause,
} from "lucide-react";
import { mediaUrl } from "@/lib/api";
import Logo from "@/components/Logo";

const SLIDE_INITIAL = { opacity: 0, scale: 1.03 };
const SLIDE_ANIMATE = { opacity: 1, scale: 1 };
const SLIDE_EXIT = { opacity: 0 };
const SLIDE_TRANSITION = {
  opacity: { duration: 0.75, ease: [0.4, 0, 0.2, 1] },
  scale: { duration: 0.9, ease: [0.4, 0, 0.2, 1] },
};

// shared by the public share link and the admin preview

export default function PresentationViewer({
  slides = [],
  title = "",
  initialIndex = 0,
  showClose = false,
  showBrand = false,
  autoPlay = false,
  autoPlayInterval = 5000,
  showPlayButton = true,
  areaNav = true,
  autoFullscreen = false,
  onClose,
  onIndexChange,
}) {
  const total = slides.length;
  const clamp = useCallback(
    (i) => Math.min(Math.max(i, 0), Math.max(total - 1, 0)),
    [total]
  );

  const [index, setIndex] = useState(() => clamp(initialIndex));
  const [active, setActive] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playing, setPlaying] = useState(autoPlay);

  const containerRef = useRef(null);
  const hideTimer = useRef(null);
  const touchStartX = useRef(null);

  const next = useCallback(
    () => setIndex((p) => (p + 1 >= total ? total - 1 : p + 1)),
    [total]
  );
  const prev = useCallback(() => setIndex((p) => (p <= 0 ? 0 : p - 1)), []);
  const first = useCallback(() => setIndex(0), []);
  const last = useCallback(() => setIndex(total - 1), [total]);

  const poke = useCallback(() => {
    setActive(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setActive(false), 3000);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    } else {
      el?.requestFullscreen?.().catch(() => {});
    }
  }, []);

  useEffect(() => {
    onIndexChange?.(index);
  }, [index, onIndexChange]);

  useEffect(() => {
    [index + 1, index - 1].forEach((i) => {
      if (i >= 0 && i < total) {
        const img = new Image();
        img.src = mediaUrl(slides[i].image_url);
      }
    });
  }, [index, slides, total]);

  useEffect(() => {
    poke();
    return () => hideTimer.current && clearTimeout(hideTimer.current);
  }, [poke]);

  useEffect(() => {
    if (!playing || total <= 1) return undefined;
    const id = setInterval(() => setIndex((p) => (p + 1) % total), autoPlayInterval);
    return () => clearInterval(id);
  }, [playing, total, autoPlayInterval]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // best-effort: browsers block fullscreen without a user gesture, so retry on first interaction
  useEffect(() => {
    if (!autoFullscreen) return undefined;
    const tryFs = () => containerRef.current?.requestFullscreen?.().catch(() => {});
    tryFs();
    const onFirst = () => {
      tryFs();
      cleanup();
    };
    const cleanup = () => {
      window.removeEventListener("pointerdown", onFirst);
      window.removeEventListener("keydown", onFirst);
      window.removeEventListener("touchstart", onFirst);
    };
    window.addEventListener("pointerdown", onFirst);
    window.addEventListener("keydown", onFirst);
    window.addEventListener("touchstart", onFirst);
    return cleanup;
  }, [autoFullscreen]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      poke();
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        prev();
      } else if (e.key === "Home") {
        e.preventDefault();
        first();
      } else if (e.key === "End") {
        e.preventDefault();
        last();
      } else if (e.key === "Escape") {
        if (!document.fullscreenElement) onClose?.();
      } else if (e.key === "f" || e.key === "F") {
        toggleFullscreen();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, first, last, poke, onClose, toggleFullscreen]);

  const onAreaClick = (e) => {
    poke();
    if (!areaNav) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    if (x < rect.width / 2) prev();
    else next();
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    poke();
    if (!areaNav) {
      touchStartX.current = null;
      return;
    }
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  if (total === 0) {
    return (
      <div
        data-testid="viewer-empty"
        className="fixed inset-0 bg-black flex items-center justify-center text-white/60 font-heading"
      >
        This presentation has no slides yet.
      </div>
    );
  }

  const current = slides[index];
  const stop = (fn) => (e) => {
    e.stopPropagation();
    fn?.();
  };

  return (
    <div
      ref={containerRef}
      data-testid="presentation-viewer"
      style={{ width: "100vw", height: "100dvh" }}
      className={`fixed top-0 left-0 bg-black overflow-hidden select-none ${
        active ? "cursor-default" : "cursor-none"
      }`}
      onMouseMove={poke}
      onClick={onAreaClick}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="absolute inset-0">
        <AnimatePresence initial={false}>
          <motion.img
            key={current.id}
            src={mediaUrl(current.image_url)}
            alt={`${title || "Presentation"} — slide ${index + 1}`}
            className="absolute inset-0 w-full h-full object-contain object-center"
            initial={SLIDE_INITIAL}
            animate={SLIDE_ANIMATE}
            exit={SLIDE_EXIT}
            transition={SLIDE_TRANSITION}
            draggable={false}
          />
        </AnimatePresence>
      </div>

      <div
        className={`pointer-events-none absolute inset-0 transition-opacity duration-300 ${
          active ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 py-4 bg-gradient-to-b from-black/60 to-transparent">
          <div className="flex items-center gap-3 min-w-0">
            {showBrand && <Logo className="h-6 opacity-80" />}
            {title && (
              <span
                data-testid="viewer-title"
                className="font-heading text-white/85 text-sm md:text-base font-medium truncate max-w-[50vw]"
              >
                {title}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 pointer-events-auto">
            <button
              data-testid="viewer-fullscreen-btn"
              aria-label="Toggle fullscreen"
              onClick={stop(toggleFullscreen)}
              className="h-10 w-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-md border border-white/10 transition-colors active:scale-95"
            >
              {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
            </button>
            {showClose && (
              <button
                data-testid="viewer-close-btn"
                aria-label="Exit presentation"
                onClick={stop(onClose)}
                className="h-10 w-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-md border border-white/10 transition-colors active:scale-95"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {total > 1 && (
          <>
            <button
              data-testid="viewer-prev-btn"
              aria-label="Previous slide"
              onClick={stop(prev)}
              disabled={index === 0}
              className="pointer-events-auto absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-md border border-white/10 transition-colors active:scale-95 disabled:opacity-25 disabled:cursor-default"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              data-testid="viewer-next-btn"
              aria-label="Next slide"
              onClick={stop(next)}
              disabled={index === total - 1}
              className="pointer-events-auto absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-md border border-white/10 transition-colors active:scale-95 disabled:opacity-25 disabled:cursor-default"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 pointer-events-auto">
          <div className="flex items-center gap-4 rounded-full bg-black/50 backdrop-blur-md text-white border border-white/10 px-5 py-2.5">
            {showPlayButton && total > 1 && (
              <button
                data-testid="viewer-play-btn"
                aria-label={playing ? "Pause slideshow" : "Play slideshow"}
                onClick={stop(() => setPlaying((p) => !p))}
                className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/15 transition-colors active:scale-95"
              >
                {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </button>
            )}
            <span
              data-testid="viewer-counter"
              className="font-heading text-sm tabular-nums tracking-wide min-w-[64px] text-center"
            >
              {index + 1} / {total}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
